import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FileText, ArrowRight, ArrowLeft, Gift, CreditCard, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { appealsAPI, paymentsAPI, plansAPI, platformFieldsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTheme } from '../App';

const PLATFORMS = [
  'Facebook', 'Instagram', 'Google', 'YouTube', 'TikTok', 'Amazon', 'Twitter/X',
  'Snapchat', 'Apple', 'Microsoft', 'Spotify', 'LinkedIn', 'Other'
];
const ISSUE_TYPES = [
  'Account Banned', 'Account Locked', 'Content Removed', 'Account Suspended',
  'Shadow Banned', 'Demonetized', 'Restricted Access', 'Failed Appeal',
  'Data Request Denied', 'Product Return', 'Unfair Removal', 'Other'
];
const COUNTRIES = [
  'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Austria',
  'Poland', 'Sweden', 'Denmark', 'Finland', 'Ireland', 'Portugal', 'Greece',
  'Czech Republic', 'Romania', 'Hungary', 'Bulgaria', 'Croatia', 'Slovakia',
  'Slovenia', 'Lithuania', 'Latvia', 'Estonia', 'Luxembourg', 'Malta', 'Cyprus'
];

export default function SubmitRequestPage({ user }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isDark = theme !== 'aurora';
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [useFree, setUseFree] = useState(false);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [form, setForm] = useState({
    platform: '', issue_type: location.state?.issueType || '',
    description: '', full_name: user?.name || '', email: user?.email || '', country: ''
  });

  useEffect(() => {
    plansAPI.list().then(res => { setPlans(res.data); if (res.data.length > 0) setSelectedPlan(res.data[0]); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.platform) {
      platformFieldsAPI.list(form.platform).then(res => {
        setDynamicFields(res.data);
        setCustomFieldValues({});
      }).catch(() => setDynamicFields([]));
    } else {
      setDynamicFields([]);
    }
  }, [form.platform]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await appealsAPI.create({ ...form, custom_fields: customFieldValues });

      if (useFree) {
        // Free appeal - call the free submit endpoint
        try {
          await paymentsAPI.submitFree(res.data.id);
          toast.success(`Appeal submitted for free! Tracking code: ${res.data.tracking_code}`);
          navigate(`/track/${res.data.tracking_code}`);
        } catch (err) {
          const msg = err.response?.data?.detail || 'Free appeal limit reached';
          toast.error(msg);
          // Fall back - let them pay
          setUseFree(false);
        }
      } else {
        // Paid appeal
        const checkoutRes = await paymentsAPI.checkout({ plan_id: selectedPlan?.id || null, appeal_id: res.data.id, origin_url: window.location.origin });
        toast.success(`Tracking: ${res.data.tracking_code}. Redirecting to payment...`);
        window.location.href = checkoutRes.data.url;
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };

  const getFieldLabel = (f) => {
    if (i18n.language === 'ar' && f.field_label_ar) return f.field_label_ar;
    if (i18n.language === 'de' && f.field_label_de) return f.field_label_de;
    return f.field_label;
  };

  const inputCl = isDark ? 'bg-white/[0.03] border-white/[0.06] text-white' : 'bg-gray-50 border-gray-200 text-gray-900';
  const dropCl = isDark ? 'bg-[#0A0A0C] border-white/10' : 'bg-white border-gray-200';
  const itemCl = isDark ? 'text-white hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-500';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';

  return (
    <div className={`min-h-screen py-20 px-6 transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: isDark ? 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(59,130,246,0.03) 0%, transparent 70%)' }} />
      <div className="max-w-2xl mx-auto relative">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-8">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold tracking-tight ${textP}`} style={{ fontFamily: 'Outfit' }}>{t('appeal.title')}</h1>
              <p className={`text-sm ${textM}`}>Step {step} of 3</p>
            </div>
          </div>
          <div className="flex gap-2 mb-8">
            {[1,2,3].map(s => (<div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-blue-500' : isDark ? 'bg-white/[0.06]' : 'bg-gray-200'}`} />))}
          </div>

          <div className={`backdrop-blur-xl rounded-2xl p-8 border ${isDark ? 'bg-[#0A0A0C]/60 border-white/[0.06]' : 'bg-white border-gray-200 shadow-xl shadow-gray-200/50'}`}>
            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <div>
                  <Label className={`text-sm mb-2 block ${textS}`}>{t('appeal.platform')}</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger className={`h-12 ${inputCl} rounded-xl`}><SelectValue placeholder={t('appeal.selectPlatform')} /></SelectTrigger>
                    <SelectContent className={dropCl}>{PLATFORMS.map(p => (<SelectItem key={p} value={p} className={itemCl}>{p}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={`text-sm mb-2 block ${textS}`}>{t('appeal.issueType')}</Label>
                  <Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}>
                    <SelectTrigger className={`h-12 ${inputCl} rounded-xl`}><SelectValue placeholder={t('appeal.selectIssue')} /></SelectTrigger>
                    <SelectContent className={dropCl}>{ISSUE_TYPES.map(i => (<SelectItem key={i} value={i} className={itemCl}>{i}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={`text-sm mb-2 block ${textS}`}>{t('appeal.description')}</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`min-h-[120px] ${inputCl} rounded-xl resize-none`} placeholder="Describe what happened..." />
                </div>

                {dynamicFields.length > 0 && (
                  <div className={`p-4 rounded-xl border space-y-4 ${isDark ? 'bg-white/[0.02] border-white/[0.04]' : 'bg-blue-50/50 border-blue-100'}`}>
                    <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{form.platform} specific fields</p>
                    {dynamicFields.map(field => (
                      <div key={field.id}>
                        <Label className={`text-sm mb-2 block ${textS}`}>{getFieldLabel(field)} {field.required && <span className="text-red-500">*</span>}</Label>
                        {field.field_type === 'textarea' ? (
                          <Textarea value={customFieldValues[field.field_name] || ''} onChange={e => setCustomFieldValues({...customFieldValues, [field.field_name]: e.target.value})} className={`min-h-[80px] ${inputCl} rounded-xl resize-none`} />
                        ) : field.field_type === 'select' ? (
                          <Select value={customFieldValues[field.field_name] || ''} onValueChange={v => setCustomFieldValues({...customFieldValues, [field.field_name]: v})}>
                            <SelectTrigger className={`h-12 ${inputCl} rounded-xl`}><SelectValue placeholder={`Select ${getFieldLabel(field)}`} /></SelectTrigger>
                            <SelectContent className={dropCl}>{(field.options || []).map(o => (<SelectItem key={o} value={o} className={itemCl}>{o}</SelectItem>))}</SelectContent>
                          </Select>
                        ) : (
                          <Input type={field.field_type} value={customFieldValues[field.field_name] || ''} onChange={e => setCustomFieldValues({...customFieldValues, [field.field_name]: e.target.value})} className={`h-12 ${inputCl} rounded-xl`} />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <Button onClick={() => setStep(2)} disabled={!form.platform || !form.issue_type || !form.description} className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-xl">
                  {t('common.next')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <div><Label className={`text-sm mb-2 block ${textS}`}>{t('appeal.fullName')}</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={`h-12 ${inputCl} rounded-xl`} /></div>
                <div><Label className={`text-sm mb-2 block ${textS}`}>{t('appeal.email')}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`h-12 ${inputCl} rounded-xl`} /></div>
                <div><Label className={`text-sm mb-2 block ${textS}`}>{t('appeal.country')}</Label>
                  <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                    <SelectTrigger className={`h-12 ${inputCl} rounded-xl`}><SelectValue placeholder="Select country" /></SelectTrigger>
                    <SelectContent className={`${dropCl} max-h-60`}>{COUNTRIES.map(c => (<SelectItem key={c} value={c} className={itemCl}>{c}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(1)} className={`flex-1 h-12 rounded-xl ${isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`}><ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}</Button>
                  <Button onClick={() => setStep(3)} disabled={!form.full_name || !form.email} className="flex-1 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-xl">{t('common.next')} <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <h3 className={`text-lg font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>{t('plans.title')}</h3>

                {/* Free Appeal Option */}
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  onClick={() => { setUseFree(true); setSelectedPlan(null); }}
                  className={`w-full text-left p-5 rounded-xl border transition-all relative overflow-hidden ${
                    useFree
                      ? isDark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-300'
                      : isDark ? 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {useFree && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    </div>
                  )}
                  <div className="flex items-start gap-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                      <Gift className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-semibold ${textP}`}>
                          {i18n.language === 'ar' ? 'طلب مجاني' : i18n.language === 'de' ? 'Kostenloser Antrag' : 'Free Appeal'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
                          {i18n.language === 'ar' ? 'مجاني' : i18n.language === 'de' ? 'Kostenlos' : 'FREE'}
                        </span>
                      </div>
                      <p className={`text-sm ${textS}`}>
                        {i18n.language === 'ar' ? 'قدّم طلبك الأول مجاناً — بدون بطاقة ائتمان' : i18n.language === 'de' ? 'Reichen Sie Ihren ersten Antrag kostenlos ein — keine Kreditkarte erforderlich' : 'Submit your first appeal for free — no credit card required'}
                      </p>
                      <div className={`flex items-center gap-3 mt-2 text-xs ${textM}`}>
                        <span>1 {i18n.language === 'ar' ? 'طلب' : i18n.language === 'de' ? 'Antrag' : 'appeal'}</span>
                        <span className={isDark ? 'text-white/10' : 'text-gray-300'}>|</span>
                        <span>{i18n.language === 'ar' ? 'تتبع الحالة' : i18n.language === 'de' ? 'Status-Tracking' : 'Status tracking'}</span>
                        <span className={isDark ? 'text-white/10' : 'text-gray-300'}>|</span>
                        <span>{i18n.language === 'ar' ? 'دعم بالبريد' : i18n.language === 'de' ? 'E-Mail-Support' : 'Email support'}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className={`text-2xl font-bold text-emerald-500`}>$0</span>
                    </div>
                  </div>
                </motion.button>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-gray-200'}`} />
                  <span className={`text-xs uppercase tracking-wider ${textM}`}>
                    {i18n.language === 'ar' ? 'أو اختر خطة' : i18n.language === 'de' ? 'oder wählen Sie einen Plan' : 'or choose a plan'}
                  </span>
                  <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-gray-200'}`} />
                </div>

                {/* Paid Plans */}
                <div className="space-y-3">
                  {plans.map((plan, i) => (
                    <motion.button key={plan.id} whileHover={{ scale: 1.01 }} onClick={() => { setSelectedPlan(plan); setUseFree(false); }}
                      className={`w-full text-left p-4 rounded-xl border transition-all relative ${
                        selectedPlan?.id === plan.id && !useFree
                          ? isDark ? 'bg-blue-500/10 border-blue-500/30' : 'bg-blue-50 border-blue-300'
                          : isDark ? 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}>
                      {selectedPlan?.id === plan.id && !useFree && (
                        <div className="absolute top-3 right-3">
                          <CheckCircle className="w-5 h-5 text-blue-500" />
                        </div>
                      )}
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                            <CreditCard className="w-4 h-4 text-blue-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold ${textP}`}>{plan.name}</span>
                              {i === 1 && <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-500 rounded-full">{t('plans.popular')}</span>}
                            </div>
                            <p className={`text-sm mt-1 ${textS}`}>{plan.description}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <span className={`text-2xl font-bold ${textP}`}>{plan.currency === 'eur' ? '€' : '$'}{plan.price}</span>
                          <span className={`text-sm ${textM}`}>{plan.billing_cycle === 'one_time' ? '' : t('plans.monthly')}</span>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(2)} className={`flex-1 h-12 rounded-xl ${isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`}><ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}</Button>
                  <Button onClick={handleSubmit} disabled={loading || (!useFree && !selectedPlan)} className={`flex-1 h-12 text-white rounded-xl ${useFree ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-blue-500 hover:bg-blue-600'}`}>
                    {loading ? t('common.loading') : useFree ? (i18n.language === 'ar' ? 'إرسال مجاناً' : i18n.language === 'de' ? 'Kostenlos einreichen' : 'Submit for Free') : t('appeal.submit')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
